import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useFinancialPlanStore } from '../store/financialPlanStore'
import { monteCarloSimulation } from '../utils/monteCarloSimulation'
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart, PieChart, Pie, Cell, ReferenceLine, Brush } from 'recharts'
import { TrendingUp, AlertTriangle, CheckCircle, Settings, Zap, FileText } from 'lucide-react'

interface SimulationResult {
  successRate: number
  medianEndingValue: number
  percentile10: number
  percentile90: number
  yearlyData: {
    year: number
    age: number
    percentile10: number
    median: number
    percentile90: number
    income: number
    expenses: number
    netCashFlow: number
  }[]
  allSimulations: number[][]
}

export function Dashboard() {
  const { clientId } = useParams()
  const navigate = useNavigate()
  const { plans, loadPlan, currentPlan } = useFinancialPlanStore()
  const [results, setResults] = useState<SimulationResult | null>(null)
  const [loading, setLoading] = useState(true)
  const [useWorker, setUseWorker] = useState(false)
  
  useEffect(() => {
    if (clientId) {
      loadPlan(clientId)
    }
  }, [clientId, loadPlan])
  
  useEffect(() => {
    if (!currentPlan) return
    setLoading(true)
    const run = async () => {
      if (useWorker) {
        try {
          const worker = new Worker(new URL('../utils/monteCarloWorker.ts', import.meta.url), { type: 'module' })
          const res: SimulationResult | null = await new Promise((resolve) => {
            worker.onmessage = (e: MessageEvent) => {
              if (e.data?.ok) resolve(e.data.result)
              else resolve(null)
              worker.terminate()
            }
            worker.postMessage(currentPlan)
          })
          setResults(res)
        } catch {
          try {
            const simulationResults = monteCarloSimulation.runSimulation(currentPlan)
            setResults(simulationResults)
          } catch {
            setResults(null)
          }
        } finally {
          setLoading(false)
        }
      } else {
        try {
          const simulationResults = monteCarloSimulation.runSimulation(currentPlan)
          setResults(simulationResults)
        } catch {
          setResults(null)
        } finally {
          setLoading(false)
        }
      }
    }
    run()
  }, [currentPlan])
  
  if (!currentPlan) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-neutral-600 mb-4">Plan not found</p>
          <button onClick={() => navigate('/')} className="btn-primary">
            Return Home
          </button>
        </div>
      </div>
    )
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-neutral-600 font-medium">Running 1,000 simulations...</p>
          <p className="text-sm text-neutral-500 mt-2">This may take a moment</p>
        </div>
      </div>
    )
  }
  
  if (!results || !results.yearlyData || results.yearlyData.length === 0) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8">
          <div className="bg-warning-100 text-warning-700 rounded-xl p-6 mb-6">
            <h3 className="font-bold text-lg mb-2">Unable to Generate Results</h3>
            <p className="text-sm">
              We need more information to run the simulation. Please make sure you've entered:
            </p>
            <ul className="text-sm text-left mt-3 space-y-1">
              <li>✓ At least one family member with birth date and retirement age</li>
              <li>✓ At least one account with a balance</li>
              <li>✓ Income sources or expenses</li>
            </ul>
          </div>
          <button onClick={() => navigate('/intake')} className="btn-primary">
            Complete Your Plan
          </button>
        </div>
      </div>
    )
  }
  
  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  
  const getSuccessColor = (rate: number) => {
    if (rate >= 85) return { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-200' }
    if (rate >= 65) return { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' }
    if (rate >= 50) return { bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-200' }
    return { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200' }
  }
  
  const successColors = getSuccessColor(results.successRate)
  
  const client = currentPlan.familyMembers.find(m => m.relationship === 'client')
  const retirementAge = client?.retirementAge || 65
  
  // Prepare chart data
  const portfolioChartData = results.yearlyData.map(d => ({
    age: d.age,
    year: d.year,
    'Worst 10%': d.percentile10,
    'Median': d.median,
    'Best 10%': d.percentile90,
  }))
  
  const cashFlowData = results.yearlyData.slice(0, 30).map(d => ({
    age: d.age,
    year: d.year,
    Income: d.income,
    Expenses: -d.expenses,
    'Net': d.netCashFlow,
  }))
  
  const totalAccounts = currentPlan.accounts.reduce((sum, acc) => sum + acc.balance, 0)
  const stocksValue = currentPlan.accounts.reduce((sum, acc) => sum + (acc.balance * acc.stocksPercent / 100), 0)
  const bondsValue = currentPlan.accounts.reduce((sum, acc) => sum + (acc.balance * acc.bondsPercent / 100), 0)
  const cashValue = currentPlan.accounts.reduce((sum, acc) => sum + (acc.balance * acc.cashPercent / 100), 0)
  
  const allocationData = [
    { name: 'Stocks', value: stocksValue, color: '#3b82f6' },
    { name: 'Bonds', value: bondsValue, color: '#10b981' },
    { name: 'Cash', value: cashValue, color: '#f59e0b' },
  ].filter(d => d.value > 0)
  
  // Histogram data at retirement age (distribution of values across simulations)
  const retirementIndex = results.yearlyData.findIndex(d => d.age === retirementAge)
  const histogramData = (() => {
    if (retirementIndex < 0) return [] as { bucket: string; count: number }[]
    const values = results.allSimulations.map(sim => sim[retirementIndex]).filter(v => typeof v === 'number')
    if (values.length === 0) return []
    const min = Math.min(...values)
    const max = Math.max(...values)
    const buckets = 12
    const size = (max - min) / buckets || 1
    const counts = Array.from({ length: buckets }, () => 0)
    for (const v of values) {
      const idx = Math.min(buckets - 1, Math.floor((v - min) / size))
      counts[idx]++
    }
    return counts.map((c, i) => ({ bucket: `$${((min + i * size) / 1000).toFixed(0)}k`, count: c }))
  })()
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Financial Plan Dashboard
              </h1>
              <p className="text-sm text-gray-600">
                {currentPlan.clientName} • Last updated: {new Date(currentPlan.updatedAt).toLocaleDateString()}
              </p>
            </div>
            <div className="flex space-x-3">
              <Link to={`/scenarios/${clientId}`} className="btn-secondary text-sm flex items-center">
                <Zap className="h-4 w-4 mr-1" />
                What-If Scenarios
              </Link>
              <Link to={`/optimizers/${clientId}`} className="btn-secondary text-sm flex items-center">
                <Settings className="h-4 w-4 mr-1" />
                Optimizers
              </Link>
              <button className="btn-primary text-sm flex items-center">
                <FileText className="h-4 w-4 mr-1" />
                Generate Report
              </button>
            </div>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Success Rate Card */}
        <div className={`${successColors.bg} border ${successColors.border} rounded-xl p-8 mb-8`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">Monte Carlo Success Rate</p>
              <div className="flex items-baseline">
                <p className={`text-6xl font-bold ${successColors.text}`}>
                  {results.successRate.toFixed(1)}%
                </p>
                {results.successRate >= 85 && <CheckCircle className={`h-8 w-8 ml-4 ${successColors.text}`} />}
                {results.successRate >= 65 && results.successRate < 85 && <TrendingUp className={`h-8 w-8 ml-4 ${successColors.text}`} />}
                {results.successRate < 65 && <AlertTriangle className={`h-8 w-8 ml-4 ${successColors.text}`} />}
              </div>
              <p className={`text-sm ${successColors.text} mt-2`}>
                {results.successRate >= 85 && 'Excellent! Your plan has a strong probability of success. You may even be over-saving.'}
                {results.successRate >= 65 && results.successRate < 85 && 'Good! Your plan is in a healthy range for retirement success.'}
                {results.successRate >= 50 && results.successRate < 65 && 'Caution: Your plan may need adjustments to improve success probability.'}
                {results.successRate < 50 && 'Action Required: Your plan needs significant adjustments.'}
              </p>
            </div>
            <div className="text-right">
              <div className="mb-4">
                <p className="text-xs text-gray-600">Median Ending Value (Age 100)</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(results.medianEndingValue)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600">Worst 10% Scenario</p>
                <p className="text-lg font-semibold text-gray-700">{formatCurrency(results.percentile10)}</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Portfolio Value Over Time */}
        <div className="card mb-8">
          <h2 className="text-xl font-bold mb-6">Lifetime Portfolio Value Projection</h2>
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={portfolioChartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="age"
                label={{ value: 'Age', position: 'insideBottom', offset: -5 }}
              />
              <YAxis
                tickFormatter={(value) => `$${(value / 1000000).toFixed(1)}M`}
                label={{ value: 'Portfolio Value', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip
                formatter={(value: number) => formatCurrency(value)}
                labelFormatter={(age) => `Age ${age}`}
              />
              <Legend />
              {retirementAge && (
                <ReferenceLine x={retirementAge} stroke="#0ea5e9" strokeDasharray="4 4" label={{ value: 'Retirement', position: 'insideTop' }} />
              )}
              <Brush dataKey="age" height={20} stroke="#94a3b8" travellerWidth={10} />
              <Area
                type="monotone"
                dataKey="Best 10%"
                stackId="1"
                stroke="#10b981"
                fill="#d1fae5"
                fillOpacity={0.6}
              />
              <Area
                type="monotone"
                dataKey="Median"
                stackId="2"
                stroke="#3b82f6"
                fill="#dbeafe"
                fillOpacity={0.8}
              />
              <Area
                type="monotone"
                dataKey="Worst 10%"
                stackId="3"
                stroke="#ef4444"
                fill="#fee2e2"
                fillOpacity={0.6}
              />
            </AreaChart>
          </ResponsiveContainer>
          {results.yearlyData && results.yearlyData.length > 0 && (
            <p className="text-sm text-gray-600 mt-4">
              This chart shows your projected portfolio value from age {results.yearlyData[0].age} to 100 across 1,000 simulations.
              The shaded areas represent the range of outcomes, with darker areas showing more likely scenarios.
            </p>
          )}
        </div>
        
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          {/* Cash Flow Chart */}
          <div className="card">
            <h2 className="text-xl font-bold mb-6">Annual Cash Flow (Next 30 Years)</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={cashFlowData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="age" />
                <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value: number) => formatCurrency(Math.abs(value))} />
                <Legend />
                <Bar dataKey="Income" fill="#10b981" />
                <Bar dataKey="Expenses" fill="#ef4444" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          
          {/* Distribution at Retirement */}
          <div className="card">
            <h2 className="text-xl font-bold mb-6">Distribution at Retirement Age</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={histogramData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="bucket" interval={0} angle={-35} textAnchor="end" height={60} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#6366f1" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Asset Allocation */}
          <div className="card">
            <h2 className="text-xl font-bold mb-6">Current Asset Allocation</h2>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={allocationData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${((entry.value / totalAccounts) * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {allocationData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-600">Total Portfolio: {formatCurrency(totalAccounts)}</p>
            </div>
          </div>
        </div>
        
        {/* Key Metrics */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="card">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Total Portfolio</h3>
            <p className="text-3xl font-bold text-gray-900">{formatCurrency(totalAccounts)}</p>
          </div>
          <div className="card">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Retirement Age</h3>
            <p className="text-3xl font-bold text-gray-900">{retirementAge}</p>
          </div>
          <div className="card">
            <h3 className="text-sm font-medium text-gray-600 mb-2">Years to Retirement</h3>
            <p className="text-3xl font-bold text-gray-900">
              {client ? Math.max(0, retirementAge - (new Date().getFullYear() - new Date(client.dateOfBirth).getFullYear())) : '-'}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
